/**
 * Sidebar Component
 * Navigation sidebar for the dashboard
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Wallet,
  PiggyBank,
  TrendingUp,
  CreditCard,
  UserCheck,
  Calculator,
  FileText,
  Bell,
  Settings,
  UserCog,
  ChevronLeft,
  LogOut,
} from 'lucide-react';

import { ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import PERMISSIONS from '../../../constants/permissions';

/**
 * Sidebar Component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Close handler for mobile
 * @returns {JSX.Element} Sidebar navigation
 */
const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { hasPermission } = usePermissions();

  const menuItems = [
    {
      path: ROUTES.DASHBOARD,
      label: 'Dashboard',
      icon: LayoutDashboard,
      permission: PERMISSIONS.DASHBOARD_VIEW,
    },
    {
      path: ROUTES.MEMBERS.LIST,
      label: 'Members',
      icon: Users,
      permission: PERMISSIONS.MEMBERS_VIEW,
    },
    {
      path: ROUTES.LOANS.LIST,
      label: 'Loans',
      icon: Wallet,
      permission: PERMISSIONS.LOANS_VIEW,
    },
    {
      path: ROUTES.SAVINGS.LIST,
      label: 'Savings',
      icon: PiggyBank,
      permission: PERMISSIONS.SAVINGS_VIEW,
    },
    {
      path: ROUTES.SHARES.LIST,
      label: 'Shares',
      icon: TrendingUp,
      permission: PERMISSIONS.SHARES_VIEW,
    },
    {
      path: ROUTES.REPAYMENTS.LIST,
      label: 'Repayments',
      icon: CreditCard,
      permission: PERMISSIONS.REPAYMENTS_VIEW,
    },
    {
      path: ROUTES.GUARANTORS.LIST,
      label: 'Guarantors',
      icon: UserCheck,
      permission: PERMISSIONS.GUARANTORS_VIEW,
    },
    {
      path: ROUTES.ACCOUNTING.DASHBOARD,
      label: 'Accounting',
      icon: Calculator,
      permission: PERMISSIONS.ACCOUNTING_VIEW,
    },
    {
      path: ROUTES.REPORTS.DASHBOARD,
      label: 'Reports',
      icon: FileText,
      permission: PERMISSIONS.REPORTS_VIEW,
    },
    {
      path: ROUTES.USERS.LIST,
      label: 'Users',
      icon: UserCog,
      permission: PERMISSIONS.USERS_VIEW,
    },
    {
      path: ROUTES.SETTINGS.GENERAL,
      label: 'Settings',
      icon: Settings,
      permission: PERMISSIONS.SETTINGS_VIEW,
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    hasPermission(item.permission)
  );

  const isActive = (path) => {
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
        <NavLink to={ROUTES.DASHBOARD} className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">US</span>
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            Uhamiaji
          </span>
        </NavLink>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className={`w-5 h-5 mr-3 ${active ? 'text-blue-600' : ''}`} />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <button
          onClick={logout}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors dark:text-red-400 dark:hover:bg-red-900/20"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
